/**
 * BioCV Design System
 * A comprehensive design system for the BioCV Web Application
 * Ensures consistent design across all components and pages
 */

// Main color palette
export const themeColors = {
	// Primary colors
	"--primary-main": "#3f51b5", // Main primary color
	"--primary-light": "#7986cb", // Lighter variation
	"--primary-dark": "#303f9f", // Darker variation
	"--primary-contrast": "#ffffff", // Text on primary background
	
	// Secondary colors
	"--secondary-main": "#20ade6", // Main secondary color
	"--secondary-light": "#6ce0ff", // Lighter variation
	"--secondary-dark": "#007eb3", // Darker variation
	"--secondary-contrast": "#ffffff", // Text on secondary background

	// Status colors
	"--success": "#4caf50", // Success messages and status
	"--warning": "#ff9800", // Warning messages and status
	"--error": "#f44336", // Error messages and status
	"--info": "#2196f3", // Information messages and status
	
	// Status colors (transparent versions for backgrounds)
	"--success-light": "rgba(76, 175, 80, 0.1)",
	"--warning-light": "rgba(255, 152, 0, 0.1)",
	"--error-light": "rgba(244, 67, 54, 0.1)",
	"--info-light": "rgba(33, 150, 243, 0.1)",
	
	// Neutrals
	"--grey-50": "#fafafa", // Very light grey (almost white)
	"--grey-100": "#f5f5f5", // Light grey
	"--grey-200": "#eeeeee", // Light grey
	"--grey-300": "#e0e0e0", // Medium light grey
	"--grey-400": "#bdbdbd", // Medium grey
	"--grey-500": "#9e9e9e", // Medium grey
	"--grey-600": "#757575", // Medium dark grey
	"--grey-700": "#616161", // Dark grey
	"--grey-800": "#424242", // Dark grey
	"--grey-900": "#212121", // Very dark grey (almost black)
	
	// Background colors
	"--background-paper": "#ffffff", // Card backgrounds
	"--background-default": "#f5f7fa", // Page background
	"--divider": "#e0e0e0", // Dividers between elements
	
	// Text colors
	"--text-primary": "#212121", // Main text
	"--text-secondary": "#757575", // Secondary/less important text
	"--text-disabled": "#9e9e9e", // Disabled text
	"--text-hint": "#9e9e9e", // Hint text

	// Legacy compatibility (maintain these for backward compatibility)
	"--main-color": "#3f51b5",
	"--main-color-highlight": "rgba(63, 81, 181, 0.1)",
	"--secondary-color": "#20ade6",
	"--secondary-background-color": "#ffffff",
	"--light-grey": "#f4f4f4",
	"--background-color": "#f5f7fa",
	"--text-color": "#212121",
	"--text-color-highlight": "#616161",
	"--red-color": "rgba(244, 67, 54, 0.6)",
};

// Version 2 Theme Colors - Second design version with a different color scheme
export const v2ThemeColors = {
	// Legacy variables (directly from user specification)
	"--main-color": "#f46c1e",
	"--main-color-highlight": "#f46c1e25",
	"--secondary-color": "#3d1f0d",
	"--secondary-background-color": "#ffffff",
	"--light-grey": "#f4f4f4",
	"--background-color": "#d7d7d7",
	"--text-color": "#1e1e1e",
	"--text-color-highlight": "rgb(79, 79, 79)",
	"--red-color": "#cc000078",
	
	// Primary colors - Mapped from legacy variables
	"--primary-main": "#f46c1e", // Using main-color (orange)
	"--primary-light": "#f68d4f", // Lighter variation of main-color
	"--primary-dark": "#b34d0e", // Darker variation of main-color
	"--primary-contrast": "#ffffff", // White text on primary background
	
	// Secondary colors - Using secondary-color
	"--secondary-main": "#3d1f0d", // Using secondary-color (dark brown)
	"--secondary-light": "#6e4634", // Lighter variation
	"--secondary-dark": "#271308", // Darker variation
	"--secondary-contrast": "#ffffff", // White text on secondary background
	
	// Status colors - Keeping similar with slight adjustments
	"--success": "#4caf50", // Success messages and status
	"--warning": "#ff9800", // Warning messages and status
	"--error": "#cc0000", // Modified to match the red-color without alpha
	"--info": "#2196f3", // Information messages and status
	
	// Status colors (transparent versions for backgrounds)
	"--success-light": "rgba(76, 175, 80, 0.1)",
	"--warning-light": "rgba(255, 152, 0, 0.1)",
	"--error-light": "#cc000025",
	"--info-light": "rgba(33, 150, 243, 0.1)",
	
	// Neutrals - Modified to match the theme
	"--grey-50": "#f9f9f9",
	"--grey-100": "#f4f4f4", // Using light-grey
	"--grey-200": "#eeeeee",
	"--grey-300": "#e0e0e0",
	"--grey-400": "#bdbdbd",
	"--grey-500": "#9e9e9e",
	"--grey-600": "rgb(79, 79, 79)", // Using text-color-highlight
	"--grey-700": "#616161", 
	"--grey-800": "#424242",
	"--grey-900": "#1e1e1e", // Using text-color
	
	// Background colors
	"--background-paper": "#ffffff", // Using secondary-background-color
	"--background-default": "#d7d7d7", // Using background-color
	"--divider": "#e0e0e0", // Standard divider
	
	// Text colors
	"--text-primary": "#1e1e1e", // Using text-color
	"--text-secondary": "rgb(79, 79, 79)", // Using text-color-highlight
	"--text-disabled": "#9e9e9e", // Standard disabled text
	"--text-hint": "#9e9e9e", // Standard hint text
};

// Version 3 Theme Colors — Förster Technik Smart Tag.
// Official brand primary: #52ae32 (leaf green). Light/dark shades and the green
// secondary are derived from it; greys are kept neutral so the green stays the hero.
export const v3ThemeColors = {
	// Legacy variables (kept in sync with the keys above)
	"--main-color": "#52ae32",
	"--main-color-highlight": "#52ae3220",
	"--secondary-color": "#2f6b1f",
	"--secondary-background-color": "#ffffff",
	"--light-grey": "#f1f5ee",
	"--background-color": "#f4f8f1",
	"--text-color": "#1f2a16",
	"--text-color-highlight": "#55624d",
	"--red-color": "rgba(220, 38, 38, 0.6)",

	// Primary colors — Förster green
	"--primary-main": "#52ae32",
	"--primary-light": "#7cc35f",
	"--primary-dark": "#3d8a24",
	"--primary-contrast": "#ffffff",

	// Secondary colors — deep green
	"--secondary-main": "#2f6b1f",
	"--secondary-light": "#58944a",
	"--secondary-dark": "#1e4a12",
	"--secondary-contrast": "#ffffff",

	// Status colors
	"--success": "#2e7d32",
	"--warning": "#f59e0b",
	"--error": "#dc2626",
	"--info": "#0ea5e9",

	// Status colors (transparent versions for backgrounds)
	"--success-light": "rgba(46, 125, 50, 0.1)",
	"--warning-light": "rgba(245, 158, 11, 0.1)",
	"--error-light": "rgba(220, 38, 38, 0.1)",
	"--info-light": "rgba(14, 165, 233, 0.1)",

	// Neutrals (kept neutral so the brand green stands out)
	"--grey-50": "#fafafa",
	"--grey-100": "#f5f5f5",
	"--grey-200": "#eeeeee",
	"--grey-300": "#e0e0e0",
	"--grey-400": "#bdbdbd",
	"--grey-500": "#9e9e9e",
	"--grey-600": "#757575",
	"--grey-700": "#616161",
	"--grey-800": "#424242",
	"--grey-900": "#212121",

	// Background colors
	"--background-paper": "#ffffff",
	"--background-default": "#f4f8f1",
	"--divider": "#e3e8de",

	// Text colors
	"--text-primary": "#1f2a16",
	"--text-secondary": "#55624d",
	"--text-disabled": "#9e9e9e",
	"--text-hint": "#9e9e9e",
};

// Spacing system
export const spacing = {
	xs: '4px',
	sm: '8px',
	md: '16px',
	lg: '24px',
	xl: '32px',
	xxl: '48px',
};

// Typography system
export const typography = {
	fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
	fontWeightLight: 300,
	fontWeightRegular: 400,
	fontWeightMedium: 500,
	fontWeightBold: 700,
	
	// Define different text sizes
	h1: {
		fontSize: '2.5rem',
		fontWeight: 700,
		lineHeight: 1.2,
	},
	h2: {
		fontSize: '2rem',
		fontWeight: 700,
		lineHeight: 1.3,
	},
	h3: {
		fontSize: '1.75rem',
		fontWeight: 600,
		lineHeight: 1.4,
	},
	h4: {
		fontSize: '1.5rem',
		fontWeight: 600,
		lineHeight: 1.4,
	},
	h5: {
		fontSize: '1.25rem',
		fontWeight: 500,
		lineHeight: 1.4,
	},
	h6: {
		fontSize: '1rem',
		fontWeight: 500,
		lineHeight: 1.4,
	},
	body1: {
		fontSize: '1rem',
		fontWeight: 400,
		lineHeight: 1.5,
	},
	body2: {
		fontSize: '0.875rem',
		fontWeight: 400,
		lineHeight: 1.5,
	},
	caption: {
		fontSize: '0.75rem',
		fontWeight: 400,
		lineHeight: 1.5,
	},
	button: {
		fontSize: '0.875rem',
		fontWeight: 500,
		lineHeight: 1.5,
		textTransform: 'uppercase',
	},
};

// Shadows for elevation
export const shadows = {
	none: 'none',
	xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
	sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
	md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
	lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
	xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// Border radiuses
export const borderRadius = {
	none: '0',
	sm: '4px',
	md: '8px',
	lg: '12px',
	xl: '16px',
	full: '9999px',
};

// Component-specific styles
export const components = {
	// Card styling
	card: {
		backgroundColor: 'var(--background-paper)',
		borderRadius: borderRadius.md,
		boxShadow: shadows.sm,
		padding: spacing.md,
		border: '1px solid var(--divider)',
		transition: 'box-shadow 0.3s ease-in-out',
		hoverStyles: {
			boxShadow: shadows.md,
		}
	},
	
	// Button styles
	button: {
		primary: {
			backgroundColor: 'var(--primary-main)',
			color: 'var(--primary-contrast)',
			padding: `${spacing.sm} ${spacing.md}`,
			borderRadius: borderRadius.sm,
			border: 'none',
			fontWeight: typography.fontWeightMedium,
			boxShadow: shadows.sm,
			transition: 'background-color 0.3s, box-shadow 0.3s',
			hoverStyles: {
				backgroundColor: 'var(--primary-dark)',
				boxShadow: shadows.md,
			},
			disabledStyles: {
				backgroundColor: 'var(--grey-300)',
				color: 'var(--text-disabled)',
			},
		},
		secondary: {
			backgroundColor: 'transparent',
			color: 'var(--primary-main)',
			padding: `${spacing.sm} ${spacing.md}`,
			borderRadius: borderRadius.sm,
			border: '1px solid var(--primary-main)',
			fontWeight: typography.fontWeightMedium,
			transition: 'background-color 0.3s, border-color 0.3s',
			hoverStyles: {
				backgroundColor: 'var(--main-color-highlight)',
			},
			disabledStyles: {
				borderColor: 'var(--grey-300)',
				color: 'var(--text-disabled)',
			},
		},
	},
	
	// Input fields
	input: {
		backgroundColor: 'var(--background-paper)',
		borderRadius: borderRadius.sm,
		border: '1px solid var(--grey-300)',
		padding: spacing.sm,
		fontSize: typography.body1.fontSize,
		transition: 'border-color 0.3s',
		focusStyles: {
			borderColor: 'var(--primary-main)',
			outline: 'none',
			boxShadow: '0 0 0 3px var(--main-color-highlight)',
		},
		errorStyles: {
			borderColor: 'var(--error)',
			boxShadow: '0 0 0 3px var(--error-light)',
		},
	},
};

// Data grid styles (building on the existing grid styles)
export const globalGridStyles = {
	borderRadius: borderRadius.md,
	boxShadow: shadows.sm,
	border: '1px solid var(--divider)',
	color: 'var(--text-primary)',
	backgroundColor: 'var(--background-paper)',
	padding: spacing.md,

	"& .MuiDataGrid-cell": {
		border: "none",
		textAlign: "center",
		padding: `${spacing.sm} ${spacing.md}`,
	},

	"& .MuiDataGrid-row:nth-of-type(even)": {
		backgroundColor: "var(--grey-50)",
	},
	"& .MuiDataGrid-row:nth-of-type(odd)": {
		backgroundColor: "var(--main-color-highlight)",
	},
	"& .MuiDataGrid-row:hover": {
		backgroundColor: "var(--main-color-highlight)",
		transition: "background-color 0.2s",
	},

	"& .MuiDataGrid-cell:hover": {
		transform: "scale(1.02)",
		transition: "transform 0.2s",
	},

	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: 'var(--light-grey)',
		color: "var(--main-color)",
		textAlign: "center",
		fontWeight: typography.fontWeightBold,
		fontSize: typography.h5.fontSize,
		padding: `${spacing.sm} ${spacing.md}`,
	},

	"& .MuiDataGrid-root::-webkit-scrollbar": {
		width: "8px",
		height: "8px",
	},
	"& .MuiDataGrid-root::-webkit-scrollbar-track": {
		background: "var(--grey-100)",
		borderRadius: "4px",
	},
	"& .MuiDataGrid-root::-webkit-scrollbar-thumb": {
		backgroundColor: "var(--primary-light)",
		borderRadius: "4px",
		border: "2px solid var(--grey-100)",
	},
	"& .MuiDataGrid-root::-webkit-scrollbar-thumb:hover": {
		backgroundColor: "var(--primary-main)",
	},

	"& .MuiDataGrid-root": {
		scrollbarWidth: "thin",
		scrollbarColor: "var(--primary-light) var(--grey-100)",
	},
};

// Status card styles (for dashboard metrics)
export const statusCardStyles = {
	display: "flex",
	alignItems: "center",
	padding: spacing.md,
	borderRadius: borderRadius.md,
	backgroundColor: "var(--background-paper)",
	boxShadow: shadows.sm,
	overflow: "hidden",
	position: "relative",
	height: "100%",
	transition: "transform 0.3s, box-shadow 0.3s",
	
	"&:hover": {
		transform: "translateY(-5px)",
		boxShadow: shadows.md,
	},
	
	// Variations for different metrics
	variants: {
		active: {
			borderLeft: "4px solid var(--primary-main)",
		},
		warning: {
			borderLeft: "4px solid var(--warning)",
		},
		alert: {
			borderLeft: "4px solid var(--error)",
		},
		neutral: {
			borderLeft: "4px solid var(--grey-500)",
		},
		success: {
			borderLeft: "4px solid var(--success)",
		},
	}
};

// Apply theme CSS variables to the root
export const applyThemeToRoot = (themeVariant = 'default') => {
	const root = document.documentElement;
	const colorSet =
		themeVariant === 'v2' ? v2ThemeColors :
		themeVariant === 'v3' ? v3ThemeColors :
		themeColors;

	Object.entries(colorSet).forEach(([key, value]) => {
		root.style.setProperty(key, value);
	});
};
